/* eslint-disable react-hooks/set-state-in-effect */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  // Set auth header from in-memory token as fallback
  useEffect(() => {
    axios.interceptors.request.use(config => {
      if (tokenRef.current && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    // Gli errori di validazione FastAPI (422) hanno `detail` come array di oggetti:
    // li trasformiamo in una stringa leggibile così i toast non ricevono mai un oggetto (crash React).
    axios.interceptors.response.use(undefined, error => {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail !== 'string') {
        const items = Array.isArray(detail) ? detail : [detail];
        const text = items.map(d => {
          if (typeof d === 'string') return d;
          const field = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body' && x !== 'query').join('.') : '';
          return field ? `${field}: ${d?.msg || 'valore non valido'}` : (d?.msg || JSON.stringify(d));
        }).join(' · ');
        error.response.data = { ...error.response.data, detail: text || 'Dati non validi', detail_raw: detail };
      }
      return Promise.reject(error);
    });
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      tokenRef.current = null;
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Store token in memory as fallback (cookie is primary)
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    }
    tokenRef.current = null;
    setUser(null);
  };

  // Admin: impersona un altro utente (dottore/paziente/operatore)
  const impersonate = async (userId) => {
    const res = await axios.post(`${API}/auth/impersonate`, { user_id: userId });
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  // Termina l'impersonificazione e ripristina la sessione admin
  const stopImpersonation = async () => {
    const res = await axios.post(`${API}/auth/stop-impersonation`);
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  const value = { user, loading, login, register, logout, fetchUser, impersonate, stopImpersonation };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
